import Script from "next/script"
import { getAllBlogArticlePaths, getBlogArticle, getGlobals, getAllBlogCategories } from "storyblok/api"
import PageHeader from "components/DynamicComponent/atoms/PageHeader"
import { CategoriesBox } from "components/Blog/CategoriesBox"
import BlogIntro from "components/Blog/BlogIntro"
import CTABanner from "components/DynamicComponent/atoms/CTABanner"
import StoryblokVisualEditor from "components/StoryblokVisualEditor"
import Image from "components/Image"
import Meta from "components/Meta"
import richText from "utilities/richText"
import { getExcerpt } from "utilities/getExcerpt"

export default function Article({ story, categories, meta }) {
  return (
    <>
      <Meta
        info={{
          title: meta?.title && meta?.title !== "" ? meta?.title : story?.content?.title,
          description:
            meta?.description && meta?.description !== "" ? meta?.description : getExcerpt(story?.content?.content),
          og_title: meta?.title && meta?.title !== "" ? meta?.title : story?.content?.title,
          og_description:
            meta?.description && meta?.description !== "" ? meta?.description : getExcerpt(story?.content?.content),
          og_image: story?.content?.featured_image?.filename ?? null,
          twitter_title: meta?.title && meta?.title !== "" ? meta?.title : story?.content?.title,
          twitter_description:
            meta?.description && meta?.description !== "" ? meta?.description : getExcerpt(story?.content?.content),
          twitter_image: story?.content?.featured_image?.filename ?? null,
        }}
      />
      <main>
        <PageHeader blok={{ heading: "Expert Blog" }} />
        <StoryblokVisualEditor story={story?.content}>
          <section className="px-8">
            <div className="lg:flex max-w-screen-xl mx-auto py-12">
              <article className="mb-12 lg:mb-0 lg:mr-24 w-full">
                <BlogIntro {...story?.content} />
                <Image
                  sizes="(max-width: 1024px) 91vw, 50vw"
                  style={{
                    width: "100%",
                    height: "auto",
                  }}
                  placeholder={story?.content?.featured_image?.blurDataURL ? "blur" : "empty"}
                  blurDataURL={story?.content?.featured_image?.blurDataURL}
                  src={
                    story?.content?.featured_image?.filename
                      ? story?.content?.featured_image?.filename
                      : "/assets/blog-placeholder.svg"
                  }
                  alt={story?.content?.featured_image?.alt ? story?.content.featured_image?.alt : ""}
                  width={710}
                  height={520}
                  className="w-full relative mb-2 lg:mb-4"
                />
                <div className="content first:prose-headings:pt-0 prose-headings:font-secondary prose-headings:font-bold first:prose-p:pt-0 prose-h2:pb-0 prose-h2:text-m1 prose-h3:text-base prose-h4:text-base prose-h5:text-base prose-h6:text-base prose-p:py-2 prose-headings:py-2 prose-ul:list-disc prose-ul:pl-4 prose-ol:list-decimal prose-ol:pl-4 marker:text-gray-dark marker:text-sm xl:max-w-4xl prose-p:font-light prose-p:leading-7">
                  {richText(story?.content?.content)}
                </div>
              </article>
              <CategoriesBox categories={categories} />
            </div>
          </section>
        </StoryblokVisualEditor>
      </main>
      <CTABanner />
      <Script type="application/ld+json" id="blog-article-schema">
        {`{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": "${story?.content?.title}",
          "author": {
            "@type": "Person",
            "name": "${story?.content?.author ? story?.content.author : "Sonoran Spine"}",
            "url": "https://www.sonoranspine.com/"
          },
          "image": [
            "${
              story?.content?.featured_image?.filename
                ? story?.content?.featured_image?.filename
                : "/assets/blog-placeholder.svg"
            }"
          ],
          "datePublished": "${story?.content?.date}",
          "dateModified": "${story?.content?.date}"
        }`}
      </Script>
    </>
  )
}

export async function getStaticProps({ params: { slug }, preview = null }) {
  const globals = await getGlobals()
  const story = await getBlogArticle(slug, preview)
  const categories = await getAllBlogCategories()

  return {
    props: {
      categories,
      story: story ?? null,
      meta: story?.content?.twentysixty_seo ?? null,
      globals: globals ?? null,
      preview,
    },
  }
}

export async function getStaticPaths() {
  return {
    paths: await getAllBlogArticlePaths(),
    fallback: false,
  }
}
